/* eslint-disable react/no-unstable-nested-components */
import * as React from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { format } from "date-fns";
import {
    Badge,
    Button,
    Card,
    DatePicker,
    Dropdown,
    DropdownContent,
    DropdownItem,
    DropdownTrigger,
    FilterButton,
    Popover,
    PopoverContent,
    PopoverTrigger,
    SearchInput,
    Table,
} from "@jhool-io/fe-components";
import { ColumnDef, ExpandedState, Row } from "@tanstack/react-table";
import { IAppCustomDates } from "../../../../utils/types";
import { NoteLabels, NoteStatus } from "../../../notes/types/notes.types";
import { useDebounce } from "../../../../hooks/helpers";
import {
    useExportProviderSessionActivityReport,
    useFetchProviderSessionActivityReport,
} from "../../hooks/queries/reports.queries";
import { ProviderCredential } from "../../../clients/types/clients.types";
import {
    convertDateFilterStringToDate,
    convertTimeInSecondsToOtherTimeUnits,
    displayNameInRightFormat,
    getValidApiDateFilter,
    formatDate,
    getCorrectTimeValueFromIsoStringInNewYorkTimeZone,
    makeStringFirstLetterCapital,
    removeEnumUnderscore,
    truncateString,
} from "../../../../utils/helpers";
import {
    IProviderSessionActivityItem,
    IProviderSessionStatsResponse,
    ISessionActivity,
} from "../../types/reports.types";
import { IApiResponse } from "../../../../utils/types/api-response";
import { fetchProviderSessionStats } from "../../services/reports.services";
import ListState from "../../../../components/ListState/ListState";
import { SessionLabels } from "../../../../utils/types/session";
import { getNoteStatusTag } from "../../../notes/helpers/notes.helpers";
import TableFiltersDisplay from "../../../../shared-ui/TableFiltersDisplay/TableFiltersDisplay";
import useToast from "../../../../hooks/useToast";
import Loader from "../../../../shared-ui/Loader/Loader";
import ChevronUpIcon from "../../../../icons/ChevronUp";
import ChevronDownIcon from "../../../../icons/ChevronDown";

interface IProviderRowBreakdown {
    [providerId: string]: {
        expanded: boolean;
        response: IApiResponse<IProviderSessionStatsResponse> | null;
        isLoading: boolean;
    };
}

export default function ProviderSessionActivity() {
    const [expandedRows, setExpandedRows] = React.useState<ExpandedState>({});
    const [providerRowBreakdown, setProviderRowBreakdown] =
        React.useState<IProviderRowBreakdown>({});
    const [isExporting, setIsExporting] = React.useState(false);
    const [searchParams, setSearchParams] = useSearchParams();

    const navigate = useNavigate();

    const toast = useToast();

    const providerNameFilter = searchParams.get("search") || "";
    const providerCredentialFilter = searchParams.get("credential") || "";
    const customDateFilter = searchParams.get("date_range") || "this_month";
    const noteStatusFilter = searchParams.getAll("note_status[]");
    const fromDateFilter = searchParams.get("from_date");
    const toDateFilter = searchParams.get("to_date");
    const fromDateCreatedFilter = searchParams.get("from_date_created");
    const toDateCreatedFilter = searchParams.get("to_date_created");
    const includeNonBillableNotesFilter =
        searchParams.get("include_non_billable_notes") || "false";

    const fromDateFilterToDate = convertDateFilterStringToDate(fromDateFilter);
    const toDateFilterToDate = convertDateFilterStringToDate(toDateFilter);
    const fromDateCreatedFilterToDate = convertDateFilterStringToDate(
        fromDateCreatedFilter
    );
    const toDateCreatedFilterToDate =
        convertDateFilterStringToDate(toDateCreatedFilter);

    const name = useDebounce(providerNameFilter, 500);

    const { data, isLoading, error, isSuccess, isFetching } =
        useFetchProviderSessionActivityReport({
            provider_name: name,
            custom_date: customDateFilter,
            provider_credential: providerCredentialFilter,
            from_date: getValidApiDateFilter(fromDateFilter),
            to_date: getValidApiDateFilter(toDateFilter),
            note_status: noteStatusFilter,
            from_created_at_date: getValidApiDateFilter(fromDateCreatedFilter),
            to_created_at_date: getValidApiDateFilter(toDateCreatedFilter),
            include_non_billable_notes: includeNonBillableNotesFilter,
        });

    const exportQuery = useExportProviderSessionActivityReport(
        {
            custom_date: customDateFilter,
            provider_name: providerNameFilter,
            provider_credential: providerCredentialFilter,
            from_date: getValidApiDateFilter(fromDateFilter),
            to_date: getValidApiDateFilter(toDateFilter),
            note_status: noteStatusFilter,
            from_created_at_date: getValidApiDateFilter(fromDateCreatedFilter),
            to_created_at_date: getValidApiDateFilter(toDateCreatedFilter),
            include_non_billable_notes: includeNonBillableNotesFilter,
        },
        isExporting
    );

    const credentialOptionsForSelect = Object.values(ProviderCredential).map(
        (credential) => {
            return credential;
        }
    );

    const customDateForSelect = Object.values(IAppCustomDates).map((item) => {
        return item;
    });

    const noteStatusForSelect = Object.values(NoteStatus)
        .filter((status) => status !== NoteStatus.DRAFT)
        .map((status) => {
            return status;
        });

    // onChange handler for search input
    const handleSetSearchString = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.value === "") searchParams.delete("search");
        else searchParams.set("search", e.target.value);
        localStorage.setItem(
            "providersactivity_filters",
            searchParams.toString()
        );
        setSearchParams(searchParams);
    };

    const handleCredentialChange = (value: string) => {
        if (value === "") searchParams.delete("credential");
        else searchParams.set("credential", value);
        searchParams.set("page", "1");
        localStorage.setItem(
            "providersactivity_filters",
            searchParams.toString()
        );
        setSearchParams(searchParams);
    };

    const handleCustomDateChange = (value: IAppCustomDates) => {
        searchParams.set("date_range", value);
        searchParams.set("page", "1");
        localStorage.setItem(
            "providersactivity_filters",
            searchParams.toString()
        );
        setSearchParams(searchParams);
    };

    const handleNoteStatusChange = (status: string) => {
        if (noteStatusFilter.includes(status)) {
            searchParams.delete("note_status[]", status);
        } else {
            searchParams.append("note_status[]", status);
        }
        searchParams.set("page", "1");
        localStorage.setItem(
            "providersactivity_filters",
            searchParams.toString()
        );
        setSearchParams(searchParams);
    };

    const handleFromDateFilterChange = (date: Date | null) => {
        if (date === null) searchParams.delete("from_date");
        else searchParams.set("from_date", format(date, "yyyy-MM-dd"));
        searchParams.set("page", "1");
        localStorage.setItem(
            "providersactivity_filters",
            searchParams.toString()
        );
        setSearchParams(searchParams);
    };

    const handleToDateFilterChange = (date: Date | null) => {
        if (date === null) searchParams.delete("to_date");
        else searchParams.set("to_date", format(date, "yyyy-MM-dd"));
        searchParams.set("page", "1");
        localStorage.setItem(
            "providersactivity_filters",
            searchParams.toString()
        );
        setSearchParams(searchParams);
    };

    const handleFromDateCreatedFilterChange = (date: Date | null) => {
        if (date === null) searchParams.delete("from_date_created");
        else searchParams.set("from_date_created", format(date, "yyyy-MM-dd"));
        searchParams.set("page", "1");
        localStorage.setItem(
            "providersactivity_filters",
            searchParams.toString()
        );
        setSearchParams(searchParams);
    };

    const handleToDateCreatedFilterChange = (date: Date | null) => {
        if (date === null) searchParams.delete("to_date_created");
        else searchParams.set("to_date_created", format(date, "yyyy-MM-dd"));
        searchParams.set("page", "1");
        localStorage.setItem(
            "providersactivity_filters",
            searchParams.toString()
        );
        setSearchParams(searchParams);
    };

    const handleIncludeNonBillableNotesFilterChange = (value: string) => {
        searchParams.set("include_non_billable_notes", value);
        searchParams.set("page", "1");
        setSearchParams(searchParams);
    };

    const handleExportClick = async () => {
        setIsExporting(true);
        await exportQuery.refetch();
        setIsExporting(false);
        if (exportQuery.error) {
            toast.toast({
                mode: "error",
                message:
                    exportQuery?.error?.message || "Could not export report.",
            });
        }
    };

    const getIncludeNonBillableNotesFilterTextToDisplay = () => {
        if (includeNonBillableNotesFilter === "true") {
            return "Include non-billable notes";
        }
        return "Show only billable notes";
    };

    const handleViewSessionNote = (clientId: string, noteId: string) => {
        navigate(`/notes/${clientId}/${noteId}`);
    };

    const getStatusTag = (item: IProviderSessionActivityItem) => {
        if (!item.note_status) {
            return <Badge content="Cancelled, No Note" type="danger" />;
        }

        return getNoteStatusTag(item.note_status);
    };

    // This function is used to update the state that stores the expanded state,
    //  response, and loading status for each provider's row breakdown.
    const setExpandedProvider = (providerId: string, isExpanded: boolean) => {
        // Use the state updater function with the current state to avoid modifying it directly.
        setProviderRowBreakdown((currentBreakdown) => {
            // Create a clone of the current breakdown state.
            const clonedBreakdown = { ...currentBreakdown };

            // Get the existing breakdown record for a particular providerId, or create one.
            const providerBreakdownRecord = clonedBreakdown[providerId] || {
                expanded: false,
                response: null,
                isLoading: false,
            };

            // Update the 'expanded' property of the breakdown with the new value.
            providerBreakdownRecord.expanded = isExpanded;

            // If the row is being expanded, update the 'isLoading' and 'response' properties.
            if (isExpanded) {
                providerBreakdownRecord.isLoading = true;
                providerBreakdownRecord.response = null;
            }

            // Update the cloned breakdown object with the modified provider breakdown values.
            clonedBreakdown[providerId] = providerBreakdownRecord;

            // Return the updated cloned breakdown object as the new state.
            return clonedBreakdown;
        });
    };

    // This function is used to refresh provider breakdown data based on specific conditions.
    const refreshProviderBreakdownData = async ({
        refreshAllOpened,
        customDate_ = IAppCustomDates.THIS_MONTH,
        providerRowBreakdown_,
        fromDate,
        toDate,
        noteStatus,
        fromDateCreated,
        toDateCreated,
        includeNonBillableNotes,
    }: {
        refreshAllOpened: boolean;
        customDate_?: IAppCustomDates;
        providerRowBreakdown_: IProviderRowBreakdown;
        fromDate?: string | null;
        toDate?: string | null;
        noteStatus?: string[];
        fromDateCreated?: string | null;
        toDateCreated?: string | null;
        includeNonBillableNotes?: string;
    }) => {
        // Create a clone of the original providerRowBreakdown_.
        const clonedProviderBreakdown = { ...providerRowBreakdown_ };

        // Determine which providerIds should be refreshed.
        const providerIdsToRefresh = Object.keys(
            clonedProviderBreakdown
        ).filter((providerId) =>
            refreshAllOpened
                ? clonedProviderBreakdown[providerId].expanded
                : clonedProviderBreakdown[providerId].expanded &&
                  clonedProviderBreakdown[providerId].response === null
        );

        // Check if there are providerIds to refresh.
        if (providerIdsToRefresh.length > 0) {
            // Use Promise.all to make API requests for each providerId.
            await Promise.all(
                providerIdsToRefresh.map(async (providerId) => {
                    // Fetch provider session stats using the provided function.
                    const response = await fetchProviderSessionStats(
                        providerId,
                        {
                            custom_date: customDate_,
                            from_date: fromDate,
                            to_date: toDate,
                            note_status: noteStatus,
                            from_created_at_date: fromDateCreated,
                            to_created_at_date: toDateCreated,
                            include_non_billable_notes: includeNonBillableNotes,
                        }
                    );

                    // Update the clonedProviderBreakdown with the new response and reset loading status.
                    clonedProviderBreakdown[providerId].response = response;
                    clonedProviderBreakdown[providerId].isLoading = false;
                })
            );

            // Update the state with the modified providerRowBreakdown.
            setProviderRowBreakdown(clonedProviderBreakdown);
        }
    };

    // these are the column items that show when a row is expanded
    const renderRowSubComponent =
        // eslint-disable-next-line react/no-unused-prop-types
        ({ row }: { row: Row<ISessionActivity> }) => {
            const providerIdToRender = row.original.provider_id;
            const providerBreakdown = providerRowBreakdown[providerIdToRender];
            const activityList =
                providerBreakdown?.response?.data.activity_list;

            return (
                <div>
                    {providerBreakdown?.isLoading && <Loader />}
                    {activityList?.length === 0 && (
                        <ListState
                            stateHelperText="Provider session activity breakdown for this provider will appear here when available"
                            isError={false}
                            emptyMessage="No breakdown is available for this provider"
                            context="general"
                        />
                    )}
                    {activityList && activityList.length > 0 && (
                        <div className="overflow-x-auto max-w-full">
                            <div className="grid grid-cols-10 gap-[--spacing-items] text-caption py-[--padding-card-y] px-[--padding-card-x] text-textSec [border-bottom:1px_solid_var(--color-stroke)]">
                                <div>CLIENT</div>
                                <div>DATE OF SERVICE</div>
                                <div>SERVICE TYPE</div>
                                <div>SESSION TIME</div>
                                <div>DURATION</div>
                                <div>NOTE TYPE</div>
                                <div>DATE CREATED</div>
                                <div>LATE NOTE</div>
                                <div className="col-[9_/_span_10]">STATUS</div>
                            </div>
                            {activityList.map((item) => (
                                <div
                                    className="grid grid-cols-10 gap-[--spacing-items] text-caption py-[--padding-card-y] px-[--padding-card-x] text-text font-medium [border-bottom:1px_solid_var(--color-stroke)] hover:bg-interactionHover hover:cursor-pointer"
                                    key={item.note_id}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        if (item.note_id) {
                                            handleViewSessionNote(
                                                item.client.client_id,
                                                item.note_id
                                            );
                                        }
                                    }}
                                    role="button"
                                    tabIndex={0}
                                    onKeyDown={(e) => {
                                        if (
                                            e.key === "Enter" ||
                                            e.key === " "
                                        ) {
                                            e.preventDefault();
                                            if (item.note_id) {
                                                handleViewSessionNote(
                                                    item.client.client_id,
                                                    item.note_id
                                                );
                                            }
                                        }
                                    }}
                                >
                                    <div>
                                        {displayNameInRightFormat({
                                            firstName: item.client.first_name,
                                            lastName: item.client.last_name,
                                        })}
                                    </div>
                                    <div>
                                        {formatDate(item.date_of_service) ||
                                            "--"}
                                    </div>
                                    <div>
                                        {SessionLabels[item.service] || "--"}
                                    </div>
                                    <div>
                                        {getCorrectTimeValueFromIsoStringInNewYorkTimeZone(
                                            item.start_date_time
                                        )}
                                    </div>
                                    <div>
                                        {convertTimeInSecondsToOtherTimeUnits(
                                            item.duration_secs
                                        )}
                                    </div>
                                    <div>
                                        {NoteLabels[item.note_type] || "--"}
                                    </div>
                                    <div>{formatDate(item.created_at)}</div>
                                    <div>
                                        {item.is_note_late ? (
                                            <Badge
                                                content="Yes"
                                                type="success"
                                            />
                                        ) : (
                                            <Badge content="No" type="danger" />
                                        )}
                                    </div>
                                    <div className="col-[9_/_span_10]">
                                        {getStatusTag(item)}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            );
        };

    const getErrorAndLoadingStates = () => {
        if (error) {
            return "0";
        }

        if (isLoading) {
            return <Loader loaderType="inline" />;
        }

        return "";
    };

    const columns: ColumnDef<ISessionActivity>[] = [
        {
            accessorKey: "first_name",
            header: "Provider name",
            cell: ({ row }) => (
                <div className="flex gap-[--spacing-items]">
                    <Button
                        variant="normal"
                        size="auto"
                        onClick={() => {
                            row.toggleExpanded();
                            setExpandedProvider(
                                row.original.provider_id,
                                !row.getIsExpanded()
                            );
                        }}
                        className="size-4 [border:1px_solid_var(--color-text)] rounded-full"
                    >
                        {row.getIsExpanded() ? (
                            <ChevronUpIcon />
                        ) : (
                            <ChevronDownIcon />
                        )}
                    </Button>

                    <Link
                        to={`/providers/${row.original.provider_id}?tab=provider-caseload`}
                        className="underline"
                    >
                        {displayNameInRightFormat({
                            firstName: row.original.first_name,
                            lastName: row.original.last_name,
                        })}
                    </Link>
                </div>
            ),
        },
        {
            accessorKey: "supervisor_first_name",
            header: "SUPERVISORS NAME",
            cell: ({ row }) =>
                row.original.supervisor_first_name ? (
                    <Link
                        to={`/providers/${row.original.supervisor_provider_id}?tab=Supervisees`}
                        className="underline"
                    >
                        {displayNameInRightFormat({
                            firstName: row.original.supervisor_first_name,
                            lastName: row.original.supervisor_last_name,
                        })}
                    </Link>
                ) : (
                    "--"
                ),
        },
        {
            accessorKey: "no_of_clients",
            header: "NO OF CLIENTS",
        },
        // {
        //     accessorKey: "sessions_completed",
        //     header: "SESSIONS COMPLETED",
        // },
        // {
        //     accessorKey: "provider_id",
        //     header: "NOTES DUE",
        //     cell: ({ row }) =>
        //         Math.max(
        //             row.original.sessions_completed -
        //                 row.original.notes_completed,
        //             0
        //         ),
        // },
        {
            accessorKey: "notes_completed",
            header: "NOTES COMPLETED",
            cell: ({ row }) => {
                return (
                    <Link
                        to={`/notes?author=${row.original.author_user_id}&status%5B%5D=${NoteStatus.CODED}&status%5B%5D=${NoteStatus.PENDING_CODE_REVIEW}`}
                        className="underline"
                    >
                        {row.original.notes_completed}
                    </Link>
                );
            },
        },
        {
            accessorKey: "notes_in_draft",
            header: "NOTES IN DRAFTS",
            cell: ({ row }) => row.original.notes_in_draft,
        },
        {
            accessorKey: "notes_in_draft",
            header: "NOTES PENDING CHANGES",
            cell: ({ row }) => row.original.notes_pending_changes,
        },
        {
            accessorKey: "notes_pending_review",
            header: "NOTES PENDING SUPERVISOR REVIEW",
            cell: ({ row }) => {
                return (
                    <Link
                        to={`/notes?author=${row.original.author_user_id}&status%5B%5D=${NoteStatus.PENDING_SUPERVISOR_REVIEW}`}
                        className="underline"
                    >
                        {row.original.notes_pending_review}
                    </Link>
                );
            },
        },
    ];

    // determine whether to refresh only the current expandedRow
    React.useEffect(() => {
        refreshProviderBreakdownData({
            refreshAllOpened: false,
            customDate_:
                (customDateFilter as IAppCustomDates) ||
                IAppCustomDates.THIS_MONTH,
            providerRowBreakdown_: providerRowBreakdown,
            fromDate: fromDateFilter,
            toDate: toDateFilter,
            noteStatus: noteStatusFilter,
            fromDateCreated: fromDateCreatedFilter,
            toDateCreated: toDateCreatedFilter,
            includeNonBillableNotes: includeNonBillableNotesFilter,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [providerRowBreakdown]);

    //  determine whether to refresh all expandedRows when date changes
    React.useEffect(() => {
        refreshProviderBreakdownData({
            refreshAllOpened: isFetching,
            customDate_:
                (customDateFilter as IAppCustomDates) ||
                IAppCustomDates.THIS_MONTH,
            providerRowBreakdown_: providerRowBreakdown,
            fromDate: fromDateFilter,
            toDate: toDateFilter,
            noteStatus: noteStatusFilter,
            fromDateCreated: fromDateCreatedFilter,
            toDateCreated: toDateCreatedFilter,
            includeNonBillableNotes: includeNonBillableNotesFilter,
        });
    }, [
        customDateFilter,
        fromDateFilter,
        isFetching,
        noteStatusFilter,
        providerRowBreakdown,
        toDateFilter,
        fromDateCreatedFilter,
        toDateCreatedFilter,
        includeNonBillableNotesFilter,
    ]);

    return (
        <div>
            <Card classNames="mb-[--spacing-header]">
                <div className="flex flex-col gap-y-[--spacing-items]">
                    <SearchInput
                        placeholder="Search by provider"
                        containerClass="w-[400px]"
                        onChange={handleSetSearchString}
                        defaultValue={providerNameFilter}
                    />
                    <div className="flex items-center gap-[--spacing-items] justify-between flex-wrap">
                        <div className="flex gap-[--spacing-items] flex-wrap">
                            <Dropdown>
                                <DropdownTrigger asChild>
                                    <FilterButton>
                                        {providerCredentialFilter
                                            ? truncateString(
                                                  removeEnumUnderscore(
                                                      providerCredentialFilter
                                                  )
                                              )
                                            : "Credential"}
                                    </FilterButton>
                                </DropdownTrigger>
                                <DropdownContent className="overflow-y-auto">
                                    <DropdownItem
                                        isSelected={!providerCredentialFilter}
                                        onClick={() =>
                                            handleCredentialChange("")
                                        }
                                    >
                                        All
                                    </DropdownItem>
                                    {credentialOptionsForSelect.map(
                                        (credential) => (
                                            <DropdownItem
                                                isSelected={
                                                    providerCredentialFilter ===
                                                    credential
                                                }
                                                key={credential}
                                                onClick={() =>
                                                    handleCredentialChange(
                                                        credential
                                                    )
                                                }
                                            >
                                                {makeStringFirstLetterCapital(
                                                    credential
                                                )}
                                            </DropdownItem>
                                        )
                                    )}
                                </DropdownContent>
                            </Dropdown>
                            <Dropdown>
                                <DropdownTrigger asChild>
                                    <FilterButton>
                                        {customDateFilter
                                            ? truncateString(
                                                  makeStringFirstLetterCapital(
                                                      removeEnumUnderscore(
                                                          customDateFilter
                                                      )
                                                  )
                                              )
                                            : "Date range"}
                                    </FilterButton>
                                </DropdownTrigger>
                                <DropdownContent align="start" width={200}>
                                    {customDateForSelect.map((range) => (
                                        <DropdownItem
                                            isSelected={
                                                customDateFilter === range
                                            }
                                            key={range}
                                            onClick={() =>
                                                handleCustomDateChange(range)
                                            }
                                        >
                                            {removeEnumUnderscore(range)}
                                        </DropdownItem>
                                    ))}
                                </DropdownContent>
                            </Dropdown>
                            <Dropdown>
                                <DropdownTrigger asChild>
                                    <FilterButton>Note status</FilterButton>
                                </DropdownTrigger>
                                <DropdownContent width="auto">
                                    {noteStatusForSelect.map((status) => (
                                        <DropdownItem
                                            key={status}
                                            onClick={() =>
                                                handleNoteStatusChange(status)
                                            }
                                            isMulti
                                            itemValue={status}
                                            values={noteStatusFilter}
                                        >
                                            {makeStringFirstLetterCapital(
                                                removeEnumUnderscore(status)
                                            )}
                                        </DropdownItem>
                                    ))}
                                </DropdownContent>
                            </Dropdown>
                            <Popover>
                                <PopoverTrigger asChild>
                                    <FilterButton>Date Of Service</FilterButton>
                                </PopoverTrigger>
                                <PopoverContent align="end">
                                    <div className="mb-1">
                                        <DatePicker
                                            label="From"
                                            placeholderText="MM/DD/YYYY"
                                            selected={fromDateFilterToDate}
                                            onChange={
                                                handleFromDateFilterChange
                                            }
                                            className="hover:border-primary"
                                            maxDate={
                                                toDateFilterToDate ||
                                                new Date(Date.now())
                                            }
                                            isClearable
                                        />
                                    </div>
                                    <DatePicker
                                        label="To"
                                        placeholderText="MM/DD/YYYY"
                                        selected={toDateFilterToDate}
                                        onChange={handleToDateFilterChange}
                                        className="hover:border-primary"
                                        minDate={fromDateFilterToDate}
                                        isClearable
                                    />
                                </PopoverContent>
                            </Popover>
                            <Popover>
                                <PopoverTrigger asChild>
                                    <FilterButton>Date Created</FilterButton>
                                </PopoverTrigger>
                                <PopoverContent align="end">
                                    <div className="mb-1">
                                        <DatePicker
                                            label="From"
                                            placeholderText="MM/DD/YYYY"
                                            selected={
                                                fromDateCreatedFilterToDate
                                            }
                                            onChange={
                                                handleFromDateCreatedFilterChange
                                            }
                                            maxDate={
                                                toDateCreatedFilterToDate ||
                                                new Date(Date.now())
                                            }
                                            isClearable
                                        />
                                    </div>
                                    <DatePicker
                                        label="To"
                                        placeholderText="MM/DD/YYYY"
                                        selected={toDateCreatedFilterToDate}
                                        onChange={
                                            handleToDateCreatedFilterChange
                                        }
                                        minDate={fromDateFilterToDate}
                                        isClearable
                                    />
                                </PopoverContent>
                            </Popover>
                            <Dropdown>
                                <DropdownTrigger asChild>
                                    <FilterButton>
                                        {getIncludeNonBillableNotesFilterTextToDisplay()}
                                    </FilterButton>
                                </DropdownTrigger>
                                <DropdownContent align="end" width="auto">
                                    <DropdownItem
                                        isSelected={
                                            includeNonBillableNotesFilter ===
                                            "false"
                                        }
                                        onClick={() =>
                                            handleIncludeNonBillableNotesFilterChange(
                                                "false"
                                            )
                                        }
                                    >
                                        Show only billable notes
                                    </DropdownItem>
                                    <DropdownItem
                                        isSelected={
                                            includeNonBillableNotesFilter ===
                                            "true"
                                        }
                                        onClick={() =>
                                            handleIncludeNonBillableNotesFilterChange(
                                                "true"
                                            )
                                        }
                                    >
                                        Include non-billable notes
                                    </DropdownItem>
                                </DropdownContent>
                            </Dropdown>
                        </div>
                        <Button
                            onClick={handleExportClick}
                            disabled={
                                data?.data.rows.length === 0 ||
                                exportQuery.isFetching
                            }
                        >
                            Export
                        </Button>
                    </div>
                </div>
                <TableFiltersDisplay
                    appliedFilters={[
                        {
                            key: "note_status[]",
                            values: noteStatusFilter,
                        },
                    ]}
                    wrapperClass="mt-[--spacing-items]"
                    localStorageKey="providersactivity_filters"
                />
            </Card>
            <Card classNames="grid grid-cols-[repeat(4,minmax(209px,1fr))] overflow-x-auto overflow-y-hidden gap-x-[--spacing-items] mb-[--spacing-header]">
                <Card classNames="bg-[--card-bg-gray] flex flex-col gap-y-2">
                    <div className="font-medium uppercase text-caption">
                        Notes Completed
                    </div>
                    <div className="font-bold text-title">
                        {getErrorAndLoadingStates() ||
                            data?.data.summary.total_notes_completed}
                    </div>
                </Card>
                <Card classNames="bg-[--card-bg-gray] flex flex-col gap-y-2">
                    <div className="font-medium uppercase text-caption">
                        Notes pending supervisor review
                    </div>
                    <div className="font-bold text-title">
                        {getErrorAndLoadingStates() ||
                            data?.data.summary.total_notes_pending_review}
                    </div>
                </Card>
                <Card classNames="bg-[--card-bg-gray] flex flex-col gap-y-2">
                    <div className="font-medium uppercase text-caption">
                        Notes in draft
                    </div>
                    <div className="font-bold text-title">
                        {getErrorAndLoadingStates() ||
                            data?.data.summary.total_notes_in_draft}
                    </div>
                </Card>
                <Card classNames="bg-[--card-bg-gray] flex flex-col gap-y-2">
                    <div className="font-medium uppercase text-caption">
                        Notes pending changes
                    </div>
                    <div className="font-bold text-title">
                        {getErrorAndLoadingStates() ||
                            data?.data.summary.total_notes_pending_changes}
                    </div>
                </Card>
            </Card>
            {isLoading && <Loader dataTestId="provideractivity-loader" />}
            {error && (
                <ListState
                    isError
                    stateHelperText="Try reloading this page to solve this issue"
                    errorMsg={
                        error?.response?.data.message ||
                        `Cannot display provider session activity list at this time please try again later`
                    }
                />
            )}
            {data && isSuccess && data.data && data.data.rows.length === 0 && (
                <ListState
                    stateHelperText="Provider session activity list will appear here once providers are added"
                    isError={false}
                    emptyMessage="No providers session activity list added yet"
                />
            )}
            {data && isSuccess && data.data && data.data.rows.length > 0 && (
                <Table
                    columns={columns}
                    data={data.data.rows}
                    isHeaderFixed
                    expandedRows={expandedRows}
                    setExpandedRows={setExpandedRows}
                    renderRowSubComponent={renderRowSubComponent}
                />
            )}
        </div>
    );
}
